import { POWERUP_TYPES, GAME_CONFIG, COLORS } from './constants.js';

export class PowerUpManager {
    constructor() {
        this.activePowerUps = [];
        this.currentMultiplier = 1;
        this.snakeColor = COLORS.PAINT_WHITE;
        this.colorChangeIndex = 0;
        this.colorChangeTime = 0;
    }
    
    reset() {
        this.activePowerUps = [];
        this.currentMultiplier = 1;
        this.snakeColor = COLORS.PAINT_WHITE;
        this.colorChangeIndex = 0;
        this.colorChangeTime = 0;
    }
    
    spawnPowerUp(gridWidth, gridHeight, snakeSegments, foodPosition) {
        if (this.hasActivePowerUp()) return;
        
        let position;
        let attempts = 0;
        const maxAttempts = 100;
        
        do {
            position = {
                x: Math.floor(Math.random() * gridWidth),
                y: Math.floor(Math.random() * gridHeight)
            };
            attempts++;
        } while (
            this.isPositionOccupied(position, snakeSegments, foodPosition) && 
            attempts < maxAttempts
        );
        
        // Choose random power-up type
        const powerUpTypes = Object.values(POWERUP_TYPES);
        const randomType = powerUpTypes[Math.floor(Math.random() * powerUpTypes.length)];
        
        const powerUp = {
            ...randomType,
            position,
            animationTime: 0,
            collected: false
        };
        
        this.activePowerUps.push(powerUp);
    }
    
    isPositionOccupied(position, snakeSegments, foodPosition) {
        // Check snake segments
        if (snakeSegments.some(segment => segment.x === position.x && segment.y === position.y)) {
            return true;
        }
        
        // Check food position
        if (foodPosition && foodPosition.x === position.x && foodPosition.y === position.y) {
            return true;
        }
        
        // Check other power-ups
        if (this.activePowerUps.some(powerUp => powerUp.position.x === position.x && powerUp.position.y === position.y)) {
            return true;
        }
        
        return false;
    }
    
    checkCollection(snakeHead) {
        for (let i = this.activePowerUps.length - 1; i >= 0; i--) {
            const powerUp = this.activePowerUps[i];
            if (powerUp.position.x === snakeHead.x && powerUp.position.y === snakeHead.y) {
                // Remove from uncollected power-ups
                this.activePowerUps.splice(i, 1);
                // Activate the power-up (this will add it back as an active power-up)
                this.activatePowerUp(powerUp);
                return powerUp;
            }
        }
        return null;
    }
    
    activatePowerUp(powerUp) {
        // Add to active power-ups with timer
        const activePowerUp = {
            ...powerUp,
            startTime: Date.now(),
            remainingTime: powerUp.duration
        };
        
        // Add to active power-ups array
        this.activePowerUps.push(activePowerUp);
        
        // Apply power-up effects
        if (powerUp.multiplier) {
            this.currentMultiplier = powerUp.multiplier;
        }
        
        if (powerUp.id === 'color_change') {
            // Color change effect - will cycle through colors
            this.colorChangeTime = 0;
        } else if (powerUp.color) {
            this.snakeColor = powerUp.color;
        }
    }
    
    update(deltaTime) {
        // Update color change animation
        if (this.getActivePowerUp('color_change') && POWERUP_TYPES.COLOR_CHANGE.colors) {
            this.colorChangeTime += deltaTime;
            // Mobile-optimized: slower color changes to reduce rendering load
            const isMobile = this.isMobileDevice();
            const changeInterval = isMobile ? 800 : 500; // Slower on mobile
            
            if (this.colorChangeTime > changeInterval) {
                const colorsLength = POWERUP_TYPES.COLOR_CHANGE.colors.length;
                if (colorsLength > 0) {
                    this.colorChangeIndex = (this.colorChangeIndex + 1) % colorsLength;
                    this.colorChangeTime = 0;
                }
            }
        }
        
        // Update active power-ups
        for (let i = this.activePowerUps.length - 1; i >= 0; i--) {
            const powerUp = this.activePowerUps[i];
            
            // Check if power-up has expired
            if (powerUp.remainingTime !== undefined) {
                powerUp.remainingTime -= deltaTime;
                if (powerUp.remainingTime <= 0) {
                    this.deactivatePowerUp(powerUp);
                    this.activePowerUps.splice(i, 1);
                }
            } else {
                // Update animation for uncollected power-ups
                powerUp.animationTime += deltaTime;
            }
        }
    }
    
    deactivatePowerUp(powerUp) {
        // Reset effects
        if (powerUp.multiplier) {
            this.currentMultiplier = 1;
        }
        
        if (powerUp.id === 'color_change') {
            this.snakeColor = COLORS.PAINT_WHITE;
            this.colorChangeIndex = 0;
        } else if (powerUp.color) {
            this.snakeColor = COLORS.PAINT_WHITE;
        }
    }
    
    getActivePowerUp(typeId) {
        return this.activePowerUps.find(powerUp => powerUp.id === typeId && powerUp.remainingTime !== undefined);
    }
    
    hasActivePowerUp() {
        return this.activePowerUps.some(powerUp => powerUp.remainingTime !== undefined);
    }
    
    getCurrentMultiplier() {
        return this.currentMultiplier;
    }
    
    getSnakeColor() {
        const colorChangePowerUp = this.getActivePowerUp('color_change');
        if (colorChangePowerUp && POWERUP_TYPES.COLOR_CHANGE.colors) {
            const colors = POWERUP_TYPES.COLOR_CHANGE.colors;
            const safeIndex = this.colorChangeIndex % colors.length;
            return colors[safeIndex];
        }
        return this.snakeColor;
    }
    
    getActiveEffects() {
        return this.activePowerUps.filter(powerUp => powerUp.remainingTime !== undefined);
    }
    
    render(ctx, gridSize) {
        // Render uncollected power-ups
        this.activePowerUps.forEach(powerUp => {
            if (!powerUp.collected && powerUp.remainingTime === undefined) {
                this.renderPowerUp(ctx, powerUp, gridSize);
            }
        });
        
        // Render active effect indicators
        this.renderActiveEffects(ctx);
    }
    
    renderPowerUp(ctx, powerUp, gridSize) {
        const x = powerUp.position.x * gridSize;
        const y = powerUp.position.y * gridSize;
        
        // Animate power-up
        const pulse = Math.sin(powerUp.animationTime * 0.008) * 0.2 + 1;
        const size = gridSize * pulse;
        const offset = (gridSize - size) / 2;
        
        // Draw glow effect
        ctx.save();
        ctx.shadowColor = powerUp.color || '#FFD700';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        // Draw power-up based on type
        if (powerUp.multiplier) {
            this.drawMultiplierPowerUp(ctx, x + offset, y + offset, size, powerUp);
        } else if (powerUp.id === 'color_change') {
            this.drawColorChangePowerUp(ctx, x + offset, y + offset, size, powerUp);
        }
        
        ctx.restore();
    }
    
    drawMultiplierPowerUp(ctx, x, y, size, powerUp) {
        const centerX = x + size / 2;
        const centerY = y + size / 2;
        
        // Paint bottle
        ctx.fillStyle = powerUp.color;
        ctx.fillRect(x + 2, y + 2, size - 4, size - 4);
        
        // Bottle cap
        ctx.fillStyle = this.darkenColor(powerUp.color, 0.3);
        ctx.fillRect(x + 1, y + 1, size - 2, size / 4);
        
        // Multiplier text
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${size / 3}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${powerUp.multiplier}x`, centerX, centerY + size / 6);
        
        // Paint spill
        ctx.fillStyle = powerUp.color;
        ctx.beginPath();
        ctx.arc(centerX, centerY + size / 3, size / 8, 0, Math.PI * 2);
        ctx.fill();
    }
    
    drawColorChangePowerUp(ctx, x, y, size, powerUp) {
        const centerX = x + size / 2;
        const centerY = y + size / 2;
        
        // Rainbow paint can
        const colors = powerUp.colors;
        const segmentHeight = size / colors.length;
        
        colors.forEach((color, index) => {
            ctx.fillStyle = color;
            ctx.fillRect(x + 2, y + 2 + index * segmentHeight, size - 4, segmentHeight);
        });
        
        // Can top
        ctx.fillStyle = '#E0E0E0';
        ctx.fillRect(x + 1, y + 1, size - 2, size / 6);
        
        // Rainbow icon
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `${size / 4}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🌈', centerX, centerY);
    }
    
    renderActiveEffects(ctx) {
        const effects = this.getActiveEffects();
        if (effects.length === 0) return;
        
        // Render effect indicators in top-right corner
        let yOffset = 60;
        effects.forEach(effect => {
            ctx.save();
            ctx.fillStyle = 'rgba(255, 215, 0, 0.8)';
            ctx.font = 'bold 14px Arial';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'middle';
            
            const timeLeft = Math.ceil(effect.remainingTime / 1000);
            const text = `${effect.icon || '⚡'} ${timeLeft}s`;
            
            // Background
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
            const textWidth = ctx.measureText(text).width;
            ctx.fillRect(ctx.canvas.width - textWidth - 20, yOffset - 10, textWidth + 16, 20);
            
            // Text
            ctx.fillStyle = '#FFD700';
            ctx.fillText(text, ctx.canvas.width - 12, yOffset);
            
            ctx.restore();
            yOffset += 25;
        });
    }
    
    darkenColor(color, amount) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        const newR = Math.floor(r * (1 - amount));
        const newG = Math.floor(g * (1 - amount));
        const newB = Math.floor(b * (1 - amount));
        
        return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    
    isMobileDevice() {
        // Detect mobile devices
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               (window.innerWidth <= 768) ||
               ('ontouchstart' in window);
    }
}
